import { defineStore } from 'pinia';
import {isAndroid, getNative} from "@/shared/helpers/webapp";
import {useNotificationStore} from "@/shared/stores";
import EscPosReceipts from "@/console-sun/helpers/EscPosReceipts";
import {useNativeStore} from "@/console-new/stores/native.store";

export const useSunmiStore = defineStore('sunmi', {
	state: () => ({
		APISCONTRINO_SENT_IDS_KEY: "apiscontrinoSentIds",
		APISCONTRINO_SENT_IDS_MAX: 200,
		printers: [],
		printerWifis: [],
		successfullConnection: false,
		failedConnection: false,
		fiscalPrinterResult: null,
		cashmaticResult: null,
		failedTapPhoneConnection: false,
		successTapPhoneConnection: false,
		failedTapPhonePayment: false,
		bluetoothUpdateProgress: null,
		// Cashmatic payment state
		cashmaticPaymentState: null, // 'started', 'completed', 'cancelled', 'committed', 'error'
		cashmaticPaymentStatus: null, // { requested, inserted, dispensed, notDispensed, status, operation }
		cashmaticPaymentResult: null, // final transaction data
		cashmaticPaymentError: null, // error message
		cashmaticActiveOperation: null, // active operation type when error code 4
		// Cashmatic device status
		cashmaticDeviceStatus: null, // { ip, totalCoins, totalBills, totalAmount, lastPing, pingSuccess }
		// Cashmatic float levels
		cashmaticFloatLevels: null, // { success, coins, notes }
		cashmaticFloatLevelSetResult: null, // { success, type, error }
		// Cashmatic all levels (current quantities)
		cashmaticAllLevels: null, // { success, coins, notes }
		// Cashmatic payment pending dismissal (user hasn't dismissed summary yet)
		cashmaticPendingDismissal: false,
		// Receipt printing queue
		receiptQueue: [],
		isReceiptPrinting: false,
		// Fiscal receipt dedup: only the device that initiated payment should print
		expectingFiscalPrint: false,
	}),
	getters: {
		canLocalPrint() {
			if (!isAndroid()) return false;
			try {
				if (typeof Android === "undefined" || typeof Android.getManufacturer !== "function") return false;
				if (Android.getManufacturer() !== "SUNMI") return false;
				if (typeof Android.getDevice !== "function") return false;
				let device = Android.getDevice().toLowerCase();
				let devicesWithPrinter = ["v1", "v1s", "v2", "v2s", "v2pro", "v3", "v3mix", "t1mini", "t2mini", "d2mini", "d3", "d3mini", "t2", "s2", "m2", "k1", "k2"];
				if (!device || !devicesWithPrinter.some(d => device.startsWith(d))) return false;
			} catch (e) {
				return false;
			}
			return true;
		},
		// Server generated content (reports, invoices) is built for 80mm paper (48 columns),
		// so a device printer set to 58mm has to ask for a narrower version.
		canLocalPrintWide() {
			return this.canLocalPrint && useNativeStore().isPrintBig;
		},
	},
	actions: {
		resetCashmaticPayment() {
			this.cashmaticPaymentState = null;
			this.cashmaticPaymentStatus = null;
			this.cashmaticPaymentResult = null;
			this.cashmaticPaymentError = null;
			this.cashmaticActiveOperation = null;
			this.cashmaticPendingDismissal = false;
		},
		saveApiscontrinoSentId(adeId) {
			let sentIds = JSON.parse(localStorage.getItem(this.APISCONTRINO_SENT_IDS_KEY) || "[]");
			if (!sentIds.includes(adeId)) {
				sentIds.push(adeId);
				if (sentIds.length > this.APISCONTRINO_SENT_IDS_MAX) sentIds = sentIds.slice(sentIds.length - this.APISCONTRINO_SENT_IDS_MAX);
				localStorage.setItem(this.APISCONTRINO_SENT_IDS_KEY, JSON.stringify(sentIds));
			}
		},
		clearApiscontrinoSentId(adeId) {
			let sentIds = JSON.parse(localStorage.getItem(this.APISCONTRINO_SENT_IDS_KEY) || "[]");
			const index = sentIds.indexOf(adeId);
			if (index !== -1) {
				sentIds.splice(index, 1);
				localStorage.setItem(this.APISCONTRINO_SENT_IDS_KEY, JSON.stringify(sentIds));
			}
		},
		isApiscontrinoIdSent(adeId) {
			let sentIds = JSON.parse(localStorage.getItem(this.APISCONTRINO_SENT_IDS_KEY) || "[]");
			return sentIds.includes(adeId);
		},
		isAlreadyPrintedAdeId(receiptAdeId, additionalLines) {
			if (!TEST && this.isApiscontrinoIdSent(receiptAdeId)) return true;
			if (!this.receiptQueue.some(queued => queued.receipt?.adeId === receiptAdeId)) return false;
			// In TEST mode all receipts share the same adeId, so also check additionalLines
			const isDuplicate = !TEST || this.receiptQueue.some(queued =>
				JSON.stringify(queued.additionalLines) === JSON.stringify(additionalLines)
			);
			if (isDuplicate && TEST) console.log("DISCARDING RECEIPT adeId:", receiptAdeId);
			return isDuplicate;
		},
		enqueuePrint(rawData) {
			this.receiptQueue.push({ rawData });
			if (!this.isReceiptPrinting) {
				this.processReceiptQueue();
			}
		},
		localPrintContent(hexContent) {
			if (!this.canLocalPrint || !hexContent) return false;
			this.enqueuePrint(EscPosReceipts.init().hexToRawString(hexContent));
			return true;
		},
		enqueueReceipt(data) {
			let adeIdToSave = null;
			if(Array.isArray(data) && data?.forEach) {
				data.forEach(singleReceipt=>{
					if (!singleReceipt?.receipt?.adeId || !this.isAlreadyPrintedAdeId(singleReceipt.receipt.adeId, singleReceipt?.additionalLines)) {
						this.receiptQueue.push(singleReceipt);
						adeIdToSave = singleReceipt?.receipt?.adeId;
					}
				});
			} else {
				if (!data?.receipt?.adeId || !this.isAlreadyPrintedAdeId(data.receipt.adeId, data?.additionalLines)) {
					this.receiptQueue.push(data);
					adeIdToSave = data?.receipt?.adeId;
				}
			}
			if (!!adeIdToSave) {
				this.saveApiscontrinoSentId(adeIdToSave);
			}
			if (!this.isReceiptPrinting) {
				this.processReceiptQueue();
			}
		},
		processReceiptQueue() {
			if (this.receiptQueue.length === 0) {
				this.isReceiptPrinting = false;
				return;
			}
			this.isReceiptPrinting = true;
			const currentItem = this.receiptQueue.shift();

			if (currentItem.rawData) {
				// Raw print job — just print, no hex building or API reporting
				const tryPrint = (retries) => {
					try {
						getNative().print(currentItem.rawData);
						setTimeout(this.processReceiptQueue, 200);
					} catch (e) {
						console.error("Sunmi raw print attempt " + (retries + 1) + " failed:", e);
						if (retries < 2) {
							setTimeout(() => tryPrint(retries + 1), 500);
						} else {
							useNotificationStore().showNotification('local-print-error');
							setTimeout(this.processReceiptQueue, 500);
						}
					}
				};
				tryPrint(0);
			} else if (currentItem?.receipt && currentItem?.business) {
				// Receipt print job
				let rawData;
				try {
					rawData = EscPosReceipts.init().getPrintReceipt(
						currentItem.business,
						currentItem.receipt,
						false,
						currentItem.additionalLines || [],
						currentItem.qrLink || null,
						currentItem.adeError || false
					);
				} catch (e) {
					console.error("Sunmi receipt rendering failed:", e);
					setTimeout(this.processReceiptQueue, 500);
					return;
				}

				const tryPrint = (retries) => {
					try {
						getNative().print(rawData);
						setTimeout(this.processReceiptQueue, 200);
					} catch (e) {
						console.error("Sunmi print attempt " + (retries + 1) + " failed:", e);
						if (retries < 2) {
							setTimeout(() => tryPrint(retries + 1), 500);
						} else {
							useNotificationStore().showNotification('local-print-error');
							setTimeout(this.processReceiptQueue, 500);
						}
					}
				};
				tryPrint(0);
			} else {
				// Invalid item, skip
				console.warn("Invalid receipt print queue item, skipping:", currentItem);
				setTimeout(this.processReceiptQueue, 200);
			}
		},
	}
});
